/**
 * GENERATED by tools/gen-theme.ts — DO NOT EDIT BY HAND.
 * Re-run `npm run gen:theme` to regenerate. One uniform manifest per ingested
 * theme (every logical key maps to "<key>.png"/"<key>.ogg" after the rename),
 * plus its own eager `?url` glob so each theme stays a self-contained, lazily-
 * loaded Pixi Assets bundle (the binaries are emitted as separate hashed assets,
 * never inlined into the initial JS chunk).
 *
 * POLISH_02 #9 EXCEPTION: candy/castle/scifi's `musicKey` below is a HAND-EDIT — the
 * generator has no spec-level musicKey input (always emits `musicKey: null`; see
 * gen-theme.ts emitGenerated) and this window was told not to re-run it. Each points at
 * a bespoke `theme_music.ogg` vendored into that theme's dir (Ludo createMusic, see
 * tools/vendor-theme-sfx.ts header). A future `npm run gen:theme` WILL wipe these 3
 * lines back to null — re-apply by hand (or promote musicKey into ThemeSpec first).
 */
import type { AssetManifest } from '../AssetManifest';

// Re-key each theme glob to the `./themes/<theme>/<file>` shape AssetManifest.
// fileUrl() looks up (the glob's own keys are `./<theme>/<file>` because this
// file lives inside themes/). Without this every generated key would miss and
// silently fall back to overworld art.
function rekey(theme: string, glob: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k in glob) out[`./themes/${theme}/${k.slice(k.lastIndexOf('/') + 1)}`] = glob[k];
  return out;
}

const CANDY_FILES = rekey('candy', import.meta.glob('./candy/*.{png,ogg}', {
  eager: true, query: '?url', import: 'default',
}) as Record<string, string>);
const CASTLE_FILES = rekey('castle', import.meta.glob('./castle/*.{png,ogg}', {
  eager: true, query: '?url', import: 'default',
}) as Record<string, string>);
const CHOCO_FILES = rekey('choco', import.meta.glob('./choco/*.{png,ogg}', {
  eager: true, query: '?url', import: 'default',
}) as Record<string, string>);
const DESERT_REMASTERED_FILES = rekey('desert-remastered', import.meta.glob('./desert-remastered/*.{png,ogg}', {
  eager: true, query: '?url', import: 'default',
}) as Record<string, string>);
const GRASSY_CLASSIC_FILES = rekey('grassy-classic', import.meta.glob('./grassy-classic/*.{png,ogg}', {
  eager: true, query: '?url', import: 'default',
}) as Record<string, string>);
const INDUSTRIAL_FILES = rekey('industrial', import.meta.glob('./industrial/*.{png,ogg}', {
  eager: true, query: '?url', import: 'default',
}) as Record<string, string>);
const SCIFI_FILES = rekey('scifi', import.meta.glob('./scifi/*.{png,ogg}', {
  eager: true, query: '?url', import: 'default',
}) as Record<string, string>);
const STONE_FILES = rekey('stone', import.meta.glob('./stone/*.{png,ogg}', {
  eager: true, query: '?url', import: 'default',
}) as Record<string, string>);
const WINTER_FILES = rekey('winter', import.meta.glob('./winter/*.{png,ogg}', {
  eager: true, query: '?url', import: 'default',
}) as Record<string, string>);

const CANDY_MANIFEST: AssetManifest = {
  theme: 'candy',
  sprites: {
    player_small_idle: 'player_small_idle.png',
    player_small_walk_1: 'player_small_walk_1.png',
    player_small_walk_2: 'player_small_walk_2.png',
    player_small_jump: 'player_small_jump.png',
    player_small_duck: 'player_small_duck.png',
    player_small_hit: 'player_small_hit.png',
    player_big_idle: 'player_big_idle.png',
    player_big_walk_1: 'player_big_walk_1.png',
    player_big_walk_2: 'player_big_walk_2.png',
    player_big_jump: 'player_big_jump.png',
    player_big_duck: 'player_big_duck.png',
    player_big_hit: 'player_big_hit.png',
    goomba_walk_1: 'goomba_walk_1.png',
    goomba_walk_2: 'goomba_walk_2.png',
    goomba_rest: 'goomba_rest.png',
    goomba_flat: 'goomba_flat.png',
    tile_solid_top: 'tile_solid_top.png',
    tile_solid_fill: 'tile_solid_fill.png',
    tile_solid_block: 'tile_solid_block.png',
    tile_semisolid: 'tile_semisolid.png',
    goal_flag_a: 'goal_flag_a.png',
    goal_flag_b: 'goal_flag_b.png',
    goal_off: 'goal_off.png',
    bg_far: 'bg_far.png',
    bg_sky: 'bg_sky.png',
  },
  sounds: {
    jump: 'jump.ogg',
    bump: 'bump.ogg',
    coin: 'coin.ogg',
    hurt: 'hurt.ogg',
    select: 'select.ogg',
  },
  musicKey: 'theme_music.ogg', // POLISH_02 #9 hand-edit — see file header
};
const CASTLE_MANIFEST: AssetManifest = {
  theme: 'castle',
  sprites: {
    player_small_idle: 'player_small_idle.png',
    player_small_walk_1: 'player_small_walk_1.png',
    player_small_walk_2: 'player_small_walk_2.png',
    player_small_jump: 'player_small_jump.png',
    player_small_duck: 'player_small_duck.png',
    player_small_hit: 'player_small_hit.png',
    player_big_idle: 'player_big_idle.png',
    player_big_walk_1: 'player_big_walk_1.png',
    player_big_walk_2: 'player_big_walk_2.png',
    player_big_jump: 'player_big_jump.png',
    player_big_duck: 'player_big_duck.png',
    player_big_hit: 'player_big_hit.png',
    goomba_walk_1: 'goomba_walk_1.png',
    goomba_walk_2: 'goomba_walk_2.png',
    goomba_rest: 'goomba_rest.png',
    goomba_flat: 'goomba_flat.png',
    tile_solid_top: 'tile_solid_top.png',
    tile_solid_fill: 'tile_solid_fill.png',
    tile_solid_block: 'tile_solid_block.png',
    tile_semisolid: 'tile_semisolid.png',
    goal_flag_a: 'goal_flag_a.png',
    goal_flag_b: 'goal_flag_b.png',
    goal_off: 'goal_off.png',
    bg_far: 'bg_far.png',
    bg_sky: 'bg_sky.png',
  },
  sounds: {
    jump: 'jump.ogg',
    bump: 'bump.ogg',
    coin: 'coin.ogg',
    hurt: 'hurt.ogg',
    select: 'select.ogg',
  },
  musicKey: 'theme_music.ogg', // POLISH_02 #9 hand-edit — see file header
};
const CHOCO_MANIFEST: AssetManifest = {
  theme: 'choco',
  sprites: {
    player_small_idle: 'player_small_idle.png',
    player_small_walk_1: 'player_small_walk_1.png',
    player_small_walk_2: 'player_small_walk_2.png',
    player_small_jump: 'player_small_jump.png',
    player_small_duck: 'player_small_duck.png',
    player_small_hit: 'player_small_hit.png',
    player_big_idle: 'player_big_idle.png',
    player_big_walk_1: 'player_big_walk_1.png',
    player_big_walk_2: 'player_big_walk_2.png',
    player_big_jump: 'player_big_jump.png',
    player_big_duck: 'player_big_duck.png',
    player_big_hit: 'player_big_hit.png',
    goomba_walk_1: 'goomba_walk_1.png',
    goomba_walk_2: 'goomba_walk_2.png',
    goomba_rest: 'goomba_rest.png',
    goomba_flat: 'goomba_flat.png',
    tile_solid_top: 'tile_solid_top.png',
    tile_solid_fill: 'tile_solid_fill.png',
    tile_solid_block: 'tile_solid_block.png',
    tile_semisolid: 'tile_semisolid.png',
    goal_flag_a: 'goal_flag_a.png',
    goal_flag_b: 'goal_flag_b.png',
    goal_off: 'goal_off.png',
    bg_far: 'bg_far.png',
    bg_sky: 'bg_sky.png',
  },
  sounds: {
    jump: 'jump.ogg',
    bump: 'bump.ogg',
    coin: 'coin.ogg',
    hurt: 'hurt.ogg',
    select: 'select.ogg',
  },
  musicKey: null,
};
const DESERT_REMASTERED_MANIFEST: AssetManifest = {
  theme: 'desert-remastered',
  sprites: {
    player_small_idle: 'player_small_idle.png',
    player_small_walk_1: 'player_small_walk_1.png',
    player_small_walk_2: 'player_small_walk_2.png',
    player_small_jump: 'player_small_jump.png',
    player_small_duck: 'player_small_duck.png',
    player_small_hit: 'player_small_hit.png',
    player_big_idle: 'player_big_idle.png',
    player_big_walk_1: 'player_big_walk_1.png',
    player_big_walk_2: 'player_big_walk_2.png',
    player_big_jump: 'player_big_jump.png',
    player_big_duck: 'player_big_duck.png',
    player_big_hit: 'player_big_hit.png',
    goomba_walk_1: 'goomba_walk_1.png',
    goomba_walk_2: 'goomba_walk_2.png',
    goomba_rest: 'goomba_rest.png',
    goomba_flat: 'goomba_flat.png',
    tile_solid_top: 'tile_solid_top.png',
    tile_solid_fill: 'tile_solid_fill.png',
    tile_solid_block: 'tile_solid_block.png',
    tile_semisolid: 'tile_semisolid.png',
    goal_flag_a: 'goal_flag_a.png',
    goal_flag_b: 'goal_flag_b.png',
    goal_off: 'goal_off.png',
    bg_far: 'bg_far.png',
    bg_sky: 'bg_sky.png',
  },
  sounds: {
    jump: 'jump.ogg',
    bump: 'bump.ogg',
    coin: 'coin.ogg',
    hurt: 'hurt.ogg',
    select: 'select.ogg',
  },
  musicKey: null,
};
const GRASSY_CLASSIC_MANIFEST: AssetManifest = {
  theme: 'grassy-classic',
  sprites: {
    player_small_idle: 'player_small_idle.png',
    player_small_walk_1: 'player_small_walk_1.png',
    player_small_walk_2: 'player_small_walk_2.png',
    player_small_jump: 'player_small_jump.png',
    player_small_duck: 'player_small_duck.png',
    player_small_hit: 'player_small_hit.png',
    player_big_idle: 'player_big_idle.png',
    player_big_walk_1: 'player_big_walk_1.png',
    player_big_walk_2: 'player_big_walk_2.png',
    player_big_jump: 'player_big_jump.png',
    player_big_duck: 'player_big_duck.png',
    player_big_hit: 'player_big_hit.png',
    goomba_walk_1: 'goomba_walk_1.png',
    goomba_walk_2: 'goomba_walk_2.png',
    goomba_rest: 'goomba_rest.png',
    goomba_flat: 'goomba_flat.png',
    tile_solid_top: 'tile_solid_top.png',
    tile_solid_fill: 'tile_solid_fill.png',
    tile_solid_block: 'tile_solid_block.png',
    tile_semisolid: 'tile_semisolid.png',
    goal_flag_a: 'goal_flag_a.png',
    goal_flag_b: 'goal_flag_b.png',
    goal_off: 'goal_off.png',
    bg_far: 'bg_far.png',
    bg_sky: 'bg_sky.png',
  },
  sounds: {
    jump: 'jump.ogg',
    bump: 'bump.ogg',
    coin: 'coin.ogg',
    hurt: 'hurt.ogg',
    select: 'select.ogg',
  },
  musicKey: null,
};
const INDUSTRIAL_MANIFEST: AssetManifest = {
  theme: 'industrial',
  sprites: {
    player_small_idle: 'player_small_idle.png',
    player_small_walk_1: 'player_small_walk_1.png',
    player_small_walk_2: 'player_small_walk_2.png',
    player_small_jump: 'player_small_jump.png',
    player_small_duck: 'player_small_duck.png',
    player_small_hit: 'player_small_hit.png',
    player_big_idle: 'player_big_idle.png',
    player_big_walk_1: 'player_big_walk_1.png',
    player_big_walk_2: 'player_big_walk_2.png',
    player_big_jump: 'player_big_jump.png',
    player_big_duck: 'player_big_duck.png',
    player_big_hit: 'player_big_hit.png',
    goomba_walk_1: 'goomba_walk_1.png',
    goomba_walk_2: 'goomba_walk_2.png',
    goomba_rest: 'goomba_rest.png',
    goomba_flat: 'goomba_flat.png',
    tile_solid_top: 'tile_solid_top.png',
    tile_solid_fill: 'tile_solid_fill.png',
    tile_solid_block: 'tile_solid_block.png',
    tile_semisolid: 'tile_semisolid.png',
    goal_flag_a: 'goal_flag_a.png',
    goal_flag_b: 'goal_flag_b.png',
    goal_off: 'goal_off.png',
    bg_far: 'bg_far.png',
    bg_sky: 'bg_sky.png',
  },
  sounds: {
    jump: 'jump.ogg',
    bump: 'bump.ogg',
    coin: 'coin.ogg',
    hurt: 'hurt.ogg',
    select: 'select.ogg',
  },
  musicKey: null,
};
const SCIFI_MANIFEST: AssetManifest = {
  theme: 'scifi',
  sprites: {
    player_small_idle: 'player_small_idle.png',
    player_small_walk_1: 'player_small_walk_1.png',
    player_small_walk_2: 'player_small_walk_2.png',
    player_small_jump: 'player_small_jump.png',
    player_small_duck: 'player_small_duck.png',
    player_small_hit: 'player_small_hit.png',
    player_big_idle: 'player_big_idle.png',
    player_big_walk_1: 'player_big_walk_1.png',
    player_big_walk_2: 'player_big_walk_2.png',
    player_big_jump: 'player_big_jump.png',
    player_big_duck: 'player_big_duck.png',
    player_big_hit: 'player_big_hit.png',
    goomba_walk_1: 'goomba_walk_1.png',
    goomba_walk_2: 'goomba_walk_2.png',
    goomba_rest: 'goomba_rest.png',
    goomba_flat: 'goomba_flat.png',
    tile_solid_top: 'tile_solid_top.png',
    tile_solid_fill: 'tile_solid_fill.png',
    tile_solid_block: 'tile_solid_block.png',
    tile_semisolid: 'tile_semisolid.png',
    goal_flag_a: 'goal_flag_a.png',
    goal_flag_b: 'goal_flag_b.png',
    goal_off: 'goal_off.png',
    bg_far: 'bg_far.png',
    bg_sky: 'bg_sky.png',
  },
  sounds: {
    jump: 'jump.ogg',
    bump: 'bump.ogg',
    coin: 'coin.ogg',
    hurt: 'hurt.ogg',
    select: 'select.ogg',
  },
  musicKey: 'theme_music.ogg', // POLISH_02 #9 hand-edit — see file header
};
const STONE_MANIFEST: AssetManifest = {
  theme: 'stone',
  sprites: {
    player_small_idle: 'player_small_idle.png',
    player_small_walk_1: 'player_small_walk_1.png',
    player_small_walk_2: 'player_small_walk_2.png',
    player_small_jump: 'player_small_jump.png',
    player_small_duck: 'player_small_duck.png',
    player_small_hit: 'player_small_hit.png',
    player_big_idle: 'player_big_idle.png',
    player_big_walk_1: 'player_big_walk_1.png',
    player_big_walk_2: 'player_big_walk_2.png',
    player_big_jump: 'player_big_jump.png',
    player_big_duck: 'player_big_duck.png',
    player_big_hit: 'player_big_hit.png',
    goomba_walk_1: 'goomba_walk_1.png',
    goomba_walk_2: 'goomba_walk_2.png',
    goomba_rest: 'goomba_rest.png',
    goomba_flat: 'goomba_flat.png',
    tile_solid_top: 'tile_solid_top.png',
    tile_solid_fill: 'tile_solid_fill.png',
    tile_solid_block: 'tile_solid_block.png',
    tile_semisolid: 'tile_semisolid.png',
    goal_flag_a: 'goal_flag_a.png',
    goal_flag_b: 'goal_flag_b.png',
    goal_off: 'goal_off.png',
    bg_far: 'bg_far.png',
    bg_sky: 'bg_sky.png',
  },
  sounds: {
    jump: 'jump.ogg',
    bump: 'bump.ogg',
    coin: 'coin.ogg',
    hurt: 'hurt.ogg',
    select: 'select.ogg',
  },
  musicKey: null,
};
const WINTER_MANIFEST: AssetManifest = {
  theme: 'winter',
  sprites: {
    player_small_idle: 'player_small_idle.png',
    player_small_walk_1: 'player_small_walk_1.png',
    player_small_walk_2: 'player_small_walk_2.png',
    player_small_jump: 'player_small_jump.png',
    player_small_duck: 'player_small_duck.png',
    player_small_hit: 'player_small_hit.png',
    player_big_idle: 'player_big_idle.png',
    player_big_walk_1: 'player_big_walk_1.png',
    player_big_walk_2: 'player_big_walk_2.png',
    player_big_jump: 'player_big_jump.png',
    player_big_duck: 'player_big_duck.png',
    player_big_hit: 'player_big_hit.png',
    goomba_walk_1: 'goomba_walk_1.png',
    goomba_walk_2: 'goomba_walk_2.png',
    goomba_rest: 'goomba_rest.png',
    goomba_flat: 'goomba_flat.png',
    tile_solid_top: 'tile_solid_top.png',
    tile_solid_fill: 'tile_solid_fill.png',
    tile_solid_block: 'tile_solid_block.png',
    tile_semisolid: 'tile_semisolid.png',
    goal_flag_a: 'goal_flag_a.png',
    goal_flag_b: 'goal_flag_b.png',
    goal_off: 'goal_off.png',
    bg_far: 'bg_far.png',
    bg_sky: 'bg_sky.png',
  },
  sounds: {
    jump: 'jump.ogg',
    bump: 'bump.ogg',
    coin: 'coin.ogg',
    hurt: 'hurt.ogg',
    select: 'select.ogg',
  },
  musicKey: null,
};

export const GENERATED_THEME_FILES: Record<string, Record<string, string>> = {
  "candy": CANDY_FILES,
  "castle": CASTLE_FILES,
  "choco": CHOCO_FILES,
  "desert-remastered": DESERT_REMASTERED_FILES,
  "grassy-classic": GRASSY_CLASSIC_FILES,
  "industrial": INDUSTRIAL_FILES,
  "scifi": SCIFI_FILES,
  "stone": STONE_FILES,
  "winter": WINTER_FILES,
};

export const GENERATED_MANIFESTS: Record<string, AssetManifest> = {
  "candy": CANDY_MANIFEST,
  "castle": CASTLE_MANIFEST,
  "choco": CHOCO_MANIFEST,
  "desert-remastered": DESERT_REMASTERED_MANIFEST,
  "grassy-classic": GRASSY_CLASSIC_MANIFEST,
  "industrial": INDUSTRIAL_MANIFEST,
  "scifi": SCIFI_MANIFEST,
  "stone": STONE_MANIFEST,
  "winter": WINTER_MANIFEST,
};

export const GENERATED_THEME_DISPLAY: Record<string, string> = {
  "candy": "Candy (cake)",
  "castle": "Castle (stone)",
  "choco": "Chocolate",
  "desert-remastered": "Desert (remastered)",
  "grassy-classic": "Grassy Classic",
  "industrial": "Industrial (metal)",
  "scifi": "Sci-Fi (metal)",
  "stone": "Stone",
  "winter": "Winter (tundra)",
};
